import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Pencil } from 'lucide-react';
import { format } from 'date-fns';

export default function MaintenanceTickets() {
  const [tickets, setTickets] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const defaultForm = { unit_id: '', title: '', description: '', priority: 'medium', status: 'open' };
  const [form, setForm] = useState(defaultForm);

  const fetchData = async () => {
    try {
      const [t, u] = await Promise.all([api.get('/maintenance-tickets'), api.get('/units')]);
      setTickets(t.data); setUnits(u.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/maintenance-tickets/${editing.id}`, form);
      else await api.post('/maintenance-tickets', form);
      setDialogOpen(false); setEditing(null); setForm(defaultForm); fetchData();
    } catch (err) { console.error(err); }
  };

  const handleEdit = (t) => {
    setEditing(t); setForm({ unit_id: t.unit_id, title: t.title, description: t.description || '', priority: t.priority, status: t.status }); setDialogOpen(true);
  };

  const handleStatusChange = async (id, status) => { await api.put(`/maintenance-tickets/${id}`, { status }); fetchData(); };

  const priorityColors = { low: 'bg-slate-100 text-slate-600', medium: 'bg-amber-50 text-amber-700 border-amber-200', high: 'bg-orange-50 text-orange-700 border-orange-200', urgent: 'bg-red-50 text-red-700 border-red-200' };
  const statusColors = { open: 'bg-blue-50 text-blue-700 border-blue-200', in_progress: 'bg-amber-50 text-amber-700 border-amber-200', resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200', closed: 'bg-slate-100 text-slate-600' };

  const filtered = filterStatus === 'all' ? tickets : tickets.filter(t => t.status === filterStatus);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" /></div>;

  return (
    <div data-testid="maintenance-page">
      <div className="page-header">
        <h1 className="page-title">Maintenance</h1>
        <div className="flex gap-3">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36" data-testid="filter-maint-status"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditing(null); setForm(defaultForm); } }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-maintenance-btn"><Plus className="w-4 h-4 mr-2" />New Ticket</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit Ticket' : 'New Maintenance Ticket'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Unit</Label>
                  <Select value={form.unit_id} onValueChange={v => setForm({ ...form, unit_id: v })}>
                    <SelectTrigger data-testid="maint-unit-select"><SelectValue placeholder="Select unit" /></SelectTrigger>
                    <SelectContent>{units.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required data-testid="maint-title-input" /></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} data-testid="maint-desc-input" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Priority</Label>
                    <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                      <SelectTrigger data-testid="maint-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="maint-submit-btn">{editing ? 'Update' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="table-wrap">
        <table className="w-full data-table min-w-[600px]">
          <thead><tr><th>Title</th><th>Unit</th><th>Priority</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
          <tbody>
            {filtered.map(t => (
              <tr key={t.id} data-testid={`maint-row-${t.id}`}>
                <td className="font-medium text-slate-900">{t.title}</td>
                <td>{t.unit_name}</td>
                <td><Badge className={priorityColors[t.priority] || ''} variant="outline">{t.priority}</Badge></td>
                <td>
                  <Select value={t.status} onValueChange={v => handleStatusChange(t.id, v)}>
                    <SelectTrigger className="h-7 text-xs w-28"><Badge className={statusColors[t.status] || ''}>{t.status?.replace('_', ' ')}</Badge></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="text-sm">{t.created_at ? format(new Date(t.created_at), 'MMM dd, yyyy') : 'N/A'}</td>
                <td><Button variant="ghost" size="sm" onClick={() => handleEdit(t)} data-testid={`edit-maint-${t.id}`}><Pencil className="w-3.5 h-3.5" /></Button></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">No tickets</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
